import React, { useState, useRef } from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import { useField } from "../form/Form";
import ErrorsForField from "../form/ErrorsForField";
import Label from "./Label";
import { createDataAttributes } from "../../utilities";

import "./Switch.scss";

const Switch = ({
  id,
  name,
  label,
  labelElement,
  value,
  className,
  disabled,
  onChange,
  onBlur,
  onValidate,
  required,
  small,
  noLabel,
  fieldErrors,
  hideErrors,
  requiredError,
  size,
  dataAttrib,
  dataWrapperAttrib,
  ...rest
}) => {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  useField({
    name,
    validation: { required, requiredError },
    onValidate,
    label
  });

  const sizeClass = size ? "uikit__input-size__" + size : null;

  const containerClasses = classnames(
    "uikit__switch",
    "uikit__form-item",
    className,
    sizeClass,
    {
      uikit__switch__disabled: disabled,
      uikit__switch__small: small,
      "uikit__form__hide-errors": hideErrors,
      "has-error": !!fieldErrors?.length,
      uikit__switch__required: required
    }
  );

  const inputClass = classnames({
    "has-error": !!fieldErrors?.length,
    "is-required": required,
    "is-checked": !!value,
    "is-focused": focused
  });

  const handleChange = event => {
    if (onChange) {
      onChange({
        event,
        field: name,
        value: !value
      });
    }
  };

  const handleBlur = event => {
    setFocused(false);
    onBlur({ event, field: name, value });
  };

  return (
    <div className={containerClasses}>
      {!noLabel && (
        <span className="uikit__label">
          <span className="uikit__label-spacer">&nbsp;</span>
        </span>
      )}
      <Label
        htmlFor={id}
        label={label}
        labelElement={labelElement}
        required={required}
        className="uikit__switch"
      >
        <span
          className="uikit__switch__slide-wrapper"
          {...createDataAttributes(dataWrapperAttrib)}
        >
          <input
            type="checkbox"
            id={id}
            name={name}
            ref={inputRef}
            value={value}
            className={inputClass}
            checked={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={() => {
              setFocused(true);
            }}
            disabled={disabled}
            {...createDataAttributes(dataAttrib)}
            {...rest}
            aria-describedby={`errors_for_${id}`}
          />
          <span className="uikit__switch__slider" />
        </span>
      </Label>
      {!hideErrors && !!fieldErrors?.length && (
        <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
      )}
    </div>
  );
};

Switch.propTypes = {
  //global form props
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onValidate: propTypes.func.isRequired,
  required: propTypes.bool,
  value: propTypes.bool.isRequired,
  noLabel: propTypes.bool,
  requiredError: propTypes.string,

  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,

  //component-specific
  onClick: propTypes.func,
  size: propTypes.oneOf([
    "auto",
    "full",
    "sm",
    "md",
    "lg",
    "25p",
    "33p",
    "50p",
    "fill"
  ]),
  small: propTypes.bool,
  componentname: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object)
};

Switch.defaultProps = {
  required: false,
  size: "auto",
  hideErrors: false,
  componentname: "Switch"
};

export default Switch;
